import React, { useState } from 'react'
import { Group } from '@visx/group'
import { LinePath } from '@visx/shape'
import { scaleLinear, scalePoint, scaleOrdinal } from '@visx/scale'
import { localPoint } from '@visx/event'
import { AxisBottom, AxisLeft } from '@visx/axis'
import { GridRows } from '@visx/grid'
import { animated, useSpring, useTransition, useSprings } from '@react-spring/web'

// Generic data interface
interface DataItem {
  category: string
  [key: string]: string | number
}

interface ChartData {
  data: DataItem[]
  keys: string[]
  colors: string[]
}

const defaultMargin = { top: 20, right: 40, bottom: 50, left: 60 }

export type LineChartProps = {
  width: number
  height: number
  margin?: typeof defaultMargin
  chartData: ChartData
  backgroundColor?: string
  tooltipUnit?: string
  xAxisLabel?: string
  yAxisLabel?: string
  showPoints?: boolean
  lineStrokeWidth?: number
  pointRadius?: number
  animate?: boolean
  hideCategoryLabelsAfter?: number
}

type TooltipData = {
  category: string
  values: Array<{
    key: string
    value: number
    color: string
  }>
  x: number
  y: number
}

export function LineChart({
  width = 500,
  height = 400,
  margin = defaultMargin,
  chartData,
  backgroundColor = '#FFF',
  tooltipUnit = '',
  xAxisLabel = 'Categories',
  yAxisLabel = 'Values',
  showPoints = true,
  lineStrokeWidth = 3,
  pointRadius = 5,
  animate = true,
  hideCategoryLabelsAfter = 10,
}: LineChartProps) {
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null)
  const [mouseX, setMouseX] = useState<number | null>(null)

  const { data, keys, colors } = chartData

  // Animation for the axes and grid - must be called before any returns
  const axisAnimation = useSpring({
    from: animate ? { opacity: 0 } : { opacity: 1 },
    to: { opacity: 1 },
    config: { tension: 300, friction: 20 },
  })

  // Animation for the legend - must be called before any returns
  const legendTransition = useTransition(Array.isArray(keys) ? keys : [], {
    from: animate ? { opacity: 0, y: 20 } : { opacity: 1, y: 0 },
    enter: { opacity: 1, y: 0 },
    trail: 100, // Stagger the animations
    config: { tension: 300, friction: 20 },
  })

  // Create line animations for all lines at once - must be called before any returns
  const lineAnimations = useSprings(
    keys?.length || 0,
    Array.isArray(keys)
      ? keys.map((_, keyIndex) => ({
          from: animate ? { pathLength: 0 } : { pathLength: 1 },
          to: { pathLength: 1 },
          delay: animate ? keyIndex * 300 : 0,
          config: { tension: 90, friction: 15 },
        }))
      : []
  )

  // Early returns after all hooks
  if (width < 10) return null
  if (!data || !Array.isArray(keys) || !colors || data.length === 0 || keys.length === 0) return null

  // Find the maximum value across all series for y-axis scaling
  const maxValue = Math.max(...data.flatMap((d) => keys.map((key) => Number(d[key]) || 0)))

  // Bounds
  const innerWidth = width - margin.left - margin.right
  const innerHeight = height - margin.top - margin.bottom

  // Scales
  const xScale = scalePoint<string>({
    domain: data.map((d) => d.category),
    range: [0, innerWidth],
    padding: 0.2,
  })

  const yScale = scaleLinear<number>({
    domain: [0, maxValue * 1.1], // Add 10% padding at the top
    range: [innerHeight, 0],
    nice: true,
  })

  const colorScale = scaleOrdinal<string, string>({
    domain: keys,
    range: colors,
  })

  // Get formatted data for display
  const getFormattedValue = (value: number) => {
    return new Intl.NumberFormat().format(value)
  }

  // Add this check near your other scale definitions
  const showCategoryLabels = data.length <= hideCategoryLabelsAfter

  return (
    <>
      <div
        className="relative"
        onMouseMove={(event) => {
          const bounds = event.currentTarget.getBoundingClientRect()
          const x = event.clientX - bounds.left - margin.left
          if (x >= 0 && x <= innerWidth) {
            setMouseX(x)
          }
        }}
        onMouseLeave={() => {
          setMouseX(null)
          setTooltipData(null)
        }}
      >
        <svg width={width} height={height}>
          <rect rx={14} width={width} height={height} fill={backgroundColor} />
          <Group top={margin.top} left={margin.left}>
            {/* Add the vertical line before other elements so it appears behind */}
            {mouseX !== null && (
              <line x1={mouseX} x2={mouseX} y1={0} y2={innerHeight} stroke="#D7D7D7" strokeWidth={1} opacity={1} />
            )}

            {/* Y-axis label with animation */}
            <animated.text
              x={-innerHeight / 2}
              y={-50}
              transform="rotate(-90)"
              textAnchor="middle"
              className="text-secondary"
              fontSize={12}
              style={{ opacity: axisAnimation.opacity }}
            >
              {yAxisLabel}
            </animated.text>

            {/* Grid with animation */}
            <animated.g style={{ opacity: axisAnimation.opacity }}>
              <GridRows
                scale={yScale}
                width={innerWidth}
                height={innerHeight}
                stroke="#e0e0e0"
                strokeDasharray="2,2"
                strokeOpacity={0.6}
              />
            </animated.g>

            {/* Add invisible bars for better hover detection */}
            {data.map((d, i) => {
              const xPos = xScale(d.category) || 0
              const barWidth = innerWidth / data.length

              return (
                <rect
                  key={`hover-bar-${i}`}
                  x={xPos - barWidth / 2}
                  y={0}
                  width={barWidth}
                  height={innerHeight}
                  fill="transparent"
                  onMouseMove={(event) => {
                    const point = localPoint(event) || { x: 0, y: 0 }
                    setMouseX(xPos)

                    // Get all values for this category
                    const values = keys.map((key) => ({
                      key,
                      value: Number(d[key]) || 0,
                      color: colorScale(key),
                    }))

                    setTooltipData({
                      category: d.category,
                      values,
                      x: point.x,
                      y: point.y,
                    })
                  }}
                  onMouseLeave={() => {
                    setMouseX(null)
                    setTooltipData(null)
                  }}
                />
              )
            })}

            {/* Render each line series */}
            {keys.map((key, keyIndex) => {
              // Create data points for this series
              const lineData = data.map((d) => ({
                x: d.category,
                y: Number(d[key]) || 0,
                category: d.category,
                key,
              }))

              return (
                <Group key={`line-${key}`}>
                  {/* Add a transparent wider path to make it easier to hover over the line */}
                  <LinePath
                    data={lineData}
                    x={(d) => xScale(d.x) || 0}
                    y={(d) => yScale(d.y)}
                    stroke="transparent"
                    strokeWidth={lineStrokeWidth * 3}
                  />

                  {/* Visible line with animation */}
                  <animated.g
                    style={{
                      strokeDasharray: lineAnimations[keyIndex].pathLength.to((x) => `${x * 10000} ${10000}`),
                    }}
                  >
                    <LinePath
                      data={lineData}
                      x={(d) => xScale(d.x) || 0}
                      y={(d) => yScale(d.y)}
                      stroke={colorScale(key)}
                      strokeWidth={lineStrokeWidth}
                    />
                  </animated.g>

                  {/* Render points on the line if showPoints is true */}
                  {showPoints &&
                    lineData.map((d, i) => {
                      // Calculate the position of this point along the line (0 to 1)
                      const pointPosition = i / (lineData.length - 1)
                      const isLastPoint = i === lineData.length - 1

                      // Make the circle appear when the line has drawn past this point
                      // Special case for the last point to ensure it appears
                      const circleOpacity = lineAnimations[keyIndex].pathLength.to((pathLength) =>
                        isLastPoint
                          ? pathLength >= 0.95
                            ? 1
                            : 0 // Last point appears when line is 95% complete
                          : pathLength >= pointPosition + 0.05
                            ? 1
                            : 0
                      )

                      return (
                        <animated.circle
                          key={`point-${key}-${i}`}
                          cx={xScale(d.x) || 0}
                          cy={yScale(d.y)}
                          r={pointRadius}
                          fill={backgroundColor}
                          stroke={colorScale(key)}
                          strokeWidth={2}
                          style={{ opacity: circleOpacity }}
                          onMouseMove={(event: React.MouseEvent<SVGCircleElement>) => {
                            const point = localPoint(event) || { x: 0, y: 0 }
                            setMouseX(xScale(d.x) || 0)

                            // Find the data point index
                            const dataIndex = data.findIndex((item) => item.category === d.category)

                            setTooltipData({
                              category: d.category,
                              values: keys.map((key) => ({
                                key,
                                value: Number(data[dataIndex][key]) || 0,
                                color: colorScale(key),
                              })),
                              x: point.x,
                              y: point.y,
                            })
                          }}
                          onMouseLeave={() => {
                            setMouseX(null)
                            setTooltipData(null)
                          }}
                        />
                      )
                    })}
                </Group>
              )
            })}

            {/* Axes with animation */}
            <animated.g style={{ opacity: axisAnimation.opacity }}>
              <AxisLeft
                scale={yScale}
                stroke="#888888"
                tickStroke="#888888"
                numTicks={6}
                tickLabelProps={{
                  className: 'text-secondary',
                  fontSize: 14,
                  textAnchor: 'end',
                  dy: '0.33em',
                }}
              />
              <AxisBottom
                top={innerHeight}
                scale={xScale}
                stroke="#888888"
                tickStroke="#888888"
                tickLabelProps={() => ({
                  fontSize: 12,
                  className: 'text-secondary',
                  textAnchor: 'middle',
                  dy: '0.33em',
                  // Hide the text if we have too many categories
                  opacity: showCategoryLabels ? 1 : 0,
                })}
              />
            </animated.g>

            {/* X-axis label with animation */}
            <animated.text
              x={innerWidth / 2}
              y={innerHeight + 50}
              textAnchor="middle"
              fontSize={12}
              className="text-primary"
              style={{ opacity: axisAnimation.opacity }}
            />
            <text x={innerWidth / 2} y={innerHeight + 45} textAnchor="middle" fontSize={12} className="text-primary">
              {xAxisLabel}
            </text>
          </Group>
        </svg>
        {tooltipData && (
          <div
            className="bg-white text-primary p-2 rounded shadow-lg text-sm pointer-events-none"
            style={{
              position: 'absolute',
              top: tooltipData.y - 10,
              left: tooltipData.x,
              transform: 'translate(-50%, -100%)',
              whiteSpace: 'nowrap',
            }}
          >
            <div className="text-primary mb-1">{tooltipData.category}</div>
            {tooltipData.values.map(({ key, value, color }) => (
              <div key={key} className="flex items-center gap-2 py-1">
                <div
                  className="w-4 h-1 shrink-0"
                  style={{
                    backgroundColor: color,
                  }}
                />
                <span className="text-secondary">{key}:</span>
                <span className="text-secondary">
                  {getFormattedValue(value)} {tooltipUnit && ` ${tooltipUnit}`}
                </span>
              </div>
            ))}
            {/* Arrow pointing down */}
            <div
              className="absolute w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white"
              style={{
                bottom: '-8px',
                left: '50%',
                marginLeft: '-6px',
                borderColor: 'bg-primary',
              }}
            />
          </div>
        )}
      </div>

      <div className="flex flex-row flex-wrap gap-4 pt-1 justify-center">
        {legendTransition((style, key, _, index) => (
          <animated.div key={key} className="flex items-center gap-2 text-lg" style={style}>
            <div
              className="w-4 h-4 shrink-0 rounded-sm"
              style={{
                backgroundColor: colors[index],
              }}
            />
            <span className="text-xs text-primary">{key}</span>
          </animated.div>
        ))}
      </div>
    </>
  )
}
